import type { HistoryPoint, HistoryRange } from "@central-hub/contracts";
import {
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  bucketLabel,
  formatLatency,
  isolatedPoints,
  linePath,
  type PointStatus,
  pointStatus,
  statusRuns,
  timeTicks,
  yTicks,
} from "./chart-math";
import { Icon } from "./icons";

/* ---------------- sparkline ---------------- */

/**
 * A small line inline in a row (24 hourly latency averages, a day of node usage). Decorative:
 * the numbers are next to it or in the detail view. `max` fixes the scale (1 for ratios);
 * by default the line fills the height.
 */
export function Sparkline({
  values,
  bad,
  max: fixedMax,
}: {
  values: readonly (number | null)[];
  bad: boolean;
  max?: number;
}) {
  const present = values.filter((v): v is number => v !== null);
  if (present.length < 2) return <span className="spark-empty" aria-hidden="true" />;
  const max = fixedMax ?? (Math.max(...present) || 1);
  const x = (i: number) => (i / (values.length - 1)) * 48;
  const y = (v: number) => 13 - (v / max) * 12;
  return (
    <svg
      className="spark"
      data-bad={bad}
      viewBox="0 0 48 14"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={linePath(values, x, y)} />
    </svg>
  );
}

/**
 * A day of a target's CPU or memory against its pods' limit (dashed): the figures beside it
 * say the same, the line shows when.
 */
export function UsageLine({
  values,
  limit,
  bad,
}: {
  values: readonly (number | null)[];
  limit: number | null;
  bad: boolean;
}) {
  const [ref, width] = useWidth(480);
  const height = 56;
  const present = values.filter((v): v is number => v !== null);
  const top = Math.max(limit ?? 0, ...present, Number.MIN_VALUE) * 1.05;
  const x = (i: number) => (i / Math.max(1, values.length - 1)) * width;
  const y = (v: number) => height - 2 - (v / top) * (height - 4);
  return (
    <span className="usage-line" ref={ref as RefObject<HTMLSpanElement>}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        data-bad={bad}
        aria-hidden="true"
      >
        {limit !== null && (
          <line className="usage-limit" x1={0} x2={width} y1={y(limit)} y2={y(limit)} />
        )}
        <path d={linePath(values, x, y)} />
      </svg>
    </span>
  );
}

/* ---------------- history chart ---------------- */

const STATUS_TEXT: Record<PointStatus, string> = {
  up: "Up",
  degraded: "Degraded",
  down: "Down",
  unknown: "Unknown",
  none: "No data",
};

/** Measures an element's width; charts re-layout on resize instead of being stretched. */
function useWidth(fallback: number) {
  const ref = useRef<HTMLElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(240, Math.floor(entry.contentRect.width)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

const M = { left: 56, right: 12, top: 10 };
const PLOT_H = 140;
const STRIP_GAP = 8;
const STRIP_H = 12;
const AXIS_H = 22;
const HEIGHT = M.top + PLOT_H + STRIP_GAP + STRIP_H + AXIS_H;

export interface HistoryChartProps {
  title: string;
  points: readonly HistoryPoint[];
  range: HistoryRange;
  from: number;
  to: number;
  bucketMs: number;
  /** Called with a bucket that had failures when the reader clicks it (or presses Enter). */
  onSelect?(index: number): void;
  /** The bucket being inspected, outlined on the status strip. */
  selected?: number | null;
}

/**
 * Average latency per bucket (line, broken where nothing was measured) over a status strip of
 * the same buckets. One crosshair and tooltip serve both; arrow keys move it for keyboard users.
 */
export function HistoryChart({
  title,
  points,
  range,
  from,
  to,
  bucketMs,
  onSelect,
  selected = null,
}: HistoryChartProps) {
  const [ref, width] = useWidth(720);
  const [hover, setHover] = useState<number | null>(null);

  const n = points.length;
  const plotW = width - M.left - M.right;
  const step = n > 0 ? plotW / n : plotW;
  const latencies = points.map((p) => p.latencyAvg);
  const present = latencies.filter((v): v is number => v !== null);
  const ticks = yTicks(present.length ? Math.max(...present) * 1.1 : 0);
  const top = ticks.at(-1) ?? 1;
  const x = (i: number) => M.left + (i + 0.5) * step;
  const y = (v: number) => M.top + PLOT_H - (v / top) * PLOT_H;
  const xTime = (t: number) => M.left + ((t - from) / (to - from)) * plotW;
  const stripY = M.top + PLOT_H + STRIP_GAP;

  const summary = present.length
    ? `${title}: average latency between ${formatLatency(Math.min(...present))} and ${formatLatency(
        Math.max(...present),
      )}; ${points.filter((p) => p.down > 0).length} of ${n} periods had failures.`
    : `${title}: no latency measured in this period.`;

  const indexAt = (clientX: number, element: Element) => {
    const left = element.getBoundingClientRect().left;
    const i = Math.floor((clientX - left - M.left) / step);
    return Math.min(n - 1, Math.max(0, i));
  };
  const onPointerMove = (event: PointerEvent<HTMLElement>) =>
    setHover(indexAt(event.clientX, event.currentTarget));
  const failed = (i: number | null) => {
    const p = i !== null ? points[i] : undefined;
    return !!p && p.down + p.degraded > 0;
  };
  const select = (i: number | null) => {
    if (onSelect && i !== null && failed(i)) onSelect(i);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      select(hover);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const delta = event.key === "ArrowLeft" ? -1 : 1;
      setHover((h) => Math.min(n - 1, Math.max(0, (h ?? n - 1) + delta)));
    } else if (event.key === "Escape") {
      setHover(null);
    }
  };

  const hovered = hover !== null ? points[hover] : undefined;
  const hoveredStatus = hovered ? pointStatus(hovered) : "none";

  return (
    <figure
      ref={ref}
      className="chart"
      // biome-ignore lint/a11y/noNoninteractiveTabindex: arrow keys move the readout
      tabIndex={0}
      aria-label={summary}
      onPointerMove={onPointerMove}
      onPointerLeave={() => setHover(null)}
      onClick={(event) => select(indexAt(event.clientX, event.currentTarget))}
      data-selectable={onSelect !== undefined && failed(hover)}
      onFocus={() => setHover((h) => h ?? n - 1)}
      onBlur={() => setHover(null)}
      onKeyDown={onKeyDown}
    >
      <svg width={width} height={HEIGHT} aria-hidden="true">
        {ticks.map((t) => (
          <g key={t}>
            <line className="grid" x1={M.left} x2={width - M.right} y1={y(t)} y2={y(t)} />
            <text className="axis" x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
              {formatLatency(t)}
            </text>
          </g>
        ))}
        <path className="series" d={linePath(latencies, x, y)} />
        {isolatedPoints(latencies).map((i) => (
          <circle key={i} className="series-dot" cx={x(i)} cy={y(latencies[i] as number)} r={3} />
        ))}
        {statusRuns(points).map((run) => (
          <rect
            key={run.start}
            className="strip"
            data-status={run.status}
            x={M.left + run.start * step + 1}
            y={stripY}
            width={Math.max(1, (run.end - run.start + 1) * step - 2)}
            height={STRIP_H}
            rx={2}
          />
        ))}
        {selected !== null && selected < n && (
          <rect
            className="strip-selected"
            x={M.left + selected * step - 1}
            y={stripY - 3}
            width={Math.max(4, step + 2)}
            height={STRIP_H + 6}
            rx={3}
          />
        )}
        {timeTicks(from, to, range).map((tick) => (
          <text key={tick.t} className="axis" x={xTime(tick.t)} y={HEIGHT - 6} textAnchor="middle">
            {tick.label}
          </text>
        ))}
        {hover !== null && hovered && (
          <g>
            <line
              className="crosshair"
              x1={x(hover)}
              x2={x(hover)}
              y1={M.top}
              y2={stripY + STRIP_H}
            />
            {hovered.latencyAvg !== null && (
              <circle className="series-dot focus" cx={x(hover)} cy={y(hovered.latencyAvg)} r={4} />
            )}
          </g>
        )}
      </svg>
      {hover !== null && hovered && (
        <div
          className="tooltip"
          role="status"
          style={{ left: Math.min(Math.max(x(hover), 90), width - 90) }}
        >
          <p className="tooltip-value num">
            {hovered.latencyAvg !== null ? formatLatency(hovered.latencyAvg) : "—"}
            <span> avg</span>
          </p>
          {hovered.latencyMax !== null && (
            <p className="num muted">max {formatLatency(hovered.latencyMax)}</p>
          )}
          <p
            className="tooltip-status"
            data-status={hoveredStatus === "none" ? "unknown" : hoveredStatus}
          >
            <Icon name={hoveredStatus === "none" ? "unknown" : hoveredStatus} />
            {STATUS_TEXT[hoveredStatus]}
            {hovered.down + hovered.degraded > 0 && (
              <span className="muted num">
                {" "}
                · {hovered.down + hovered.degraded} of{" "}
                {hovered.up + hovered.degraded + hovered.down} checks
              </span>
            )}
          </p>
          <p className="muted num">{bucketLabel(Date.parse(hovered.t), bucketMs)}</p>
          {onSelect && failed(hover) && <p className="tooltip-hint">Click for details</p>}
        </div>
      )}
    </figure>
  );
}

/** Status legend shared by every chart in a view: shape + text, never color alone. */
export function StatusLegend() {
  const items: PointStatus[] = ["up", "degraded", "down", "none"];
  return (
    <ul className="legend" aria-label="Status strip legend">
      {items.map((status) => (
        <li key={status} data-status={status === "none" ? "unknown" : status}>
          <span className="legend-swatch" data-status={status} aria-hidden="true" />
          {STATUS_TEXT[status]}
        </li>
      ))}
    </ul>
  );
}
